import { DateTime } from "luxon";
import { type FC, useEffect, useEffectEvent, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useUserSettingsContext } from "../../contexts/Staffing/UserSettingsContext";
import { useWeekIndex } from "../../contexts/WeekIndexProvider";
import { useWeekViewContext } from "../../contexts/WeekView";
import { useScheduleCacheEnabled } from "../../hooks/useScheduleWeekRules";
import { useShownWeek } from "../../hooks/useShownWeek";
import { SynergyModeStaffing } from "../../pages/SynergyModeStaffing/SynergyModeStaffing";
import { getWeekStart } from "../../utils/dateUtils";
import { DAYS_PER_WEEK, DEFAULT_WEEK_START_DAY, WeeklyViewOption } from "../../utils/models";
import { toIsoDate } from "../../utils/scheduleWeeks";
import { staffingWeeklyPath } from "../../utils/staffingRoutes";
import { ScheduleHolidaysProvider } from "../team/Holidays/schedule/ScheduleHolidaysProvider";
import { type AppliedRouteWeek, rememberedRouteWeek, resolveAppliedRouteWeek } from "./appliedRouteWeek";
import { writeLastStaffingView } from "./MonthlyView/jobView/monthlyViewPreferences";
import Staffing from "./Staffing";
import Staffingv2 from "./Staffingv2";

/**
 * The staffing grid for this department type. The other views of the Staffing area (Day, Month, and the
 * People roster) are their own routes, so this only has to pick a grid.
 */
const StaffingAreaContent: FC = () => {
	const { settings } = useUserSettingsContext();
	const { isMedicalDepartment } = useHospitalData();
	const { firstDate, lastDate } = useWeekViewContext();

	// The department-type dispatch: synergy mode first (before settings.view), then the v2/v1 grids.
	if (isMedicalDepartment) return <SynergyModeStaffing />;
	// Only the weekly grids show holidays, so only they load them.
	return (
		<ScheduleHolidaysProvider firstDate={firstDate} lastDate={lastDate}>
			{settings.view === WeeklyViewOption.V2 ? <Staffingv2 /> : <Staffing />}
		</ScheduleHolidaysProvider>
	);
};

export const StaffingWrapper: FC = () => {
	const { dateString } = useParams<{ dateString?: string }>();
	const { hospitalData, getWeekIndexByDate, timezone, chosenDepartment } = useHospitalData();
	const { weekIndex, setWeekIndex } = useWeekIndex();
	const { weekStart, goToWeek } = useShownWeek();
	const isCacheEnabled = useScheduleCacheEnabled();
	const { firstDate, lastDate } = useWeekViewContext();
	const departmentId = chosenDepartment?.department_id;
	const { track } = useAnalytics();

	const trackPageLoad = useEffectEvent(() => {
		track("staffing-page-load");
	});
	useEffect(() => {
		trackPageLoad();
		// Remember that the scheduler last used the weekly grid, so the Monthly view (APP-465) can honor
		// "remember the last view" on a future landing. NOTE: the PRD's landing-redirect that defaults new
		// users to Monthly is intentionally deferred — it belongs at the Staffing nav entry point, not
		// here, so switching from Monthly back to Weekly can't bounce the user straight back to Monthly.
		writeLastStaffingView("weekly");
	}, []);

	// With the cache on the URL's date is the shown week, loaded or not; a department switch keeps the date. Only when the
	// date or the department changes: the arrows move the address bar with `replaceState`, which `useParams` doesn't see,
	// so `dateString` can be a week the user paged away from.
	const showWeekOf = useEffectEvent((date: DateTime) => goToWeek(date));
	useEffect(() => {
		if (isCacheEnabled && dateString && departmentId) showWeekOf(DateTime.fromISO(dateString, { zone: timezone }));
	}, [isCacheEnabled, dateString, timezone, departmentId]);

	// A URL week the schedule doesn't reach (a typo, an old bookmark) opens today's week, as with the cache off, once the
	// schedule's range is known; the address bar follows, as it follows Today. Checked once per URL date: the arrows
	// page with `replaceState`, so the date stays the typed one and a later check would pull the grid back to today.
	const checkedRouteRef = useRef<string | null>(null);
	useEffect(() => {
		if (!isCacheEnabled || !dateString || !departmentId || firstDate === undefined || lastDate === undefined) return;
		const routeKey = `${departmentId}/${dateString}`;
		if (checkedRouteRef.current === routeKey) return;
		checkedRouteRef.current = routeKey;
		const routeWeekStart = getWeekStart(DateTime.fromISO(dateString, { zone: timezone }), DEFAULT_WEEK_START_DAY);
		const isInSchedule =
			routeWeekStart.isValid &&
			toIsoDate(routeWeekStart.plus({ days: DAYS_PER_WEEK - 1 })) >= toIsoDate(firstDate) &&
			toIsoDate(routeWeekStart) <= toIsoDate(lastDate);
		if (isInSchedule) return;
		const today = DateTime.now().setZone(timezone);
		showWeekOf(today);
		window.history.replaceState(null, "", staffingWeeklyPath(departmentId, toIsoDate(getWeekStart(today, DEFAULT_WEEK_START_DAY))));
	}, [isCacheEnabled, dateString, departmentId, firstDate, lastDate, timezone]);

	// With the cache off, the route date is applied once it can be resolved in the loaded weeks. Week arrows move the
	// visible week with `replaceState`, which does not change `dateString`, so a later weeks refresh
	// must not pull the grid back to the route. Accept on the census notice is one such refresh: it
	// rebuilds the weeks array, `getWeekIndexByDate` changes, and re-applying the route date jumped
	// the schedule to another week instead of leaving the open menu's week in place.
	const appliedRouteWeekRef = useRef<AppliedRouteWeek | null>(null);
	useEffect(() => {
		if (isCacheEnabled) return;
		const remembered = rememberedRouteWeek(dateString, appliedRouteWeekRef.current);
		if (!dateString) {
			appliedRouteWeekRef.current = remembered;
			return;
		}
		// Parsed IN the department's zone, not converted into it afterwards. `fromISO` reads a bare
		// `yyyy-MM-dd` at the BROWSER's midnight, so `.setZone(timezone)` then walks it backwards for any
		// browser ahead of the department — far enough to land on the previous day, and so on the previous
		// week whenever the date is a week start. That only became reachable once a department switch
		// started preserving the date it was looking at.
		const date = DateTime.fromISO(dateString, { zone: timezone });
		const next = resolveAppliedRouteWeek(remembered, {
			dateString,
			departmentId: chosenDepartment?.department_id ?? "",
			weekIndex,
			calculatedWeekIndex: getWeekIndexByDate(date),
		});
		if (!next) return;

		appliedRouteWeekRef.current = next;
		if (weekIndex !== next.appliedIndex) setWeekIndex(next.appliedIndex);
	}, [isCacheEnabled, dateString, getWeekIndexByDate, timezone, setWeekIndex, chosenDepartment?.department_id, weekIndex]);

	// Which week to open on when the URL doesn't name a date -- signing in, or landing on the bare
	// /staffing route. `weekIndex` starts at 0, and week 0 is NOT the current week: the server returns
	// `department_data_history_weeks` of history before the date it was asked for, so week 0 sits weeks
	// in the past. That is why login landed on a five-week-old date in a department the user had never
	// chosen. Snapped once per department rather than on every render of the effect, because the URL
	// write below uses `replaceState` -- which doesn't refresh `useParams` -- so `dateString` stays
	// undefined afterwards and a standing rule here would fight the week arrows.
	const snappedDepartmentRef = useRef<string | null>(null);
	useEffect(() => {
		if (dateString || !departmentId || snappedDepartmentRef.current === departmentId) return;
		// With the cache on today's week is a date, so it needs no loaded weeks.
		if (isCacheEnabled) {
			snappedDepartmentRef.current = departmentId;
			showWeekOf(DateTime.now().setZone(timezone));
			return;
		}
		if (hospitalData.weeks.length === 0) return;

		// Claim the one-shot only once the snap actually lands. A non-empty `weeks` array does not mean the
		// week containing today has hydrated -- `getWeekIndexByDate` guards against a partially-built week
		// and returns -1 -- so marking the department first would spend the single attempt on a pass that
		// did nothing, and the grid would sit on week 0 (history) for the rest of the session.
		const todaysWeekIndex = getWeekIndexByDate(DateTime.now().setZone(timezone));
		if (todaysWeekIndex === -1) return;

		snappedDepartmentRef.current = departmentId;
		setWeekIndex(todaysWeekIndex);
	}, [dateString, departmentId, isCacheEnabled, hospitalData.weeks.length, getWeekIndexByDate, timezone, setWeekIndex]);

	// Update URL with current week's start date if no dateString in URL
	const weekStartIso = weekStart?.toFormat("yyyy-MM-dd");
	useEffect(() => {
		if (!dateString && weekStartIso && chosenDepartment?.department_id) {
			const expectedPath = staffingWeeklyPath(chosenDepartment.department_id, weekStartIso);
			// Only update if the current path doesn't match
			if (window.location.pathname !== expectedPath) {
				window.history.replaceState(null, "", expectedPath);
			}
		}
	}, [dateString, weekStartIso, chosenDepartment]);

	return <StaffingAreaContent />;
};
